import { Select } from "antd"
import { H5 } from "components"
import { LoadingRegion } from "components/custom/Loader"
import { CardListSkeleton } from "components/new-stuff/ui/Skeleton"
import { useEffect, useRef, useState } from "react"
import calenderPlus from "assets/svg/CalendarPlus.svg"
import upIcon from "assets/svg/chevron-up.svg"
import downIcon from "assets/svg/chevron-down.svg"
import rightIcon from "assets/illustrations/right-arrow.svg"

import EmptyImg from "../../../../assets/illustrations/timesheet-empty-img.svg"
import {
  IShiftSetupRes,
  ITimeSheetRes,
  IAttendanceRecord,
} from "../interface/shiftData"
import {
  formatGroupLabel,
  formatDay,
  formatTime,
  getFlags,
  sortByDate,
} from "../utils"
import { ManualEntryModal } from "./ManualEntryModal"
import TimesheetCollapseRow from "./TimesheetCollapseRow"
import { TimesheetRow } from "./TimesheetRow"
import { TimesheetEntryOptionsModal } from "./TimesheetEntryOptionsModal"

export const Timesheets = ({
  shiftSetupData,
  groupBy,
  timesheetQueryKey,
  timesheetData,
  isLoadingTimesheet,
  setGroupBy,
}: {
  shiftSetupData: IShiftSetupRes | undefined
  groupBy: "day" | "week" | "month"
  timesheetQueryKey: string
  timesheetData: ITimeSheetRes | undefined
  isLoadingTimesheet: boolean
  setGroupBy: (body: "day" | "week" | "month") => void
}) => {
  const [activeShiftCollapseKeys, setActiveShiftCollapseKeys] = useState("")
  const [showAddReasonModal, setShowAddReasonModal] = useState(false)
  const [showManualEntry, setShowManualEntry] = useState(false)
  const [showOptions, setShowOptions] = useState(false)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const lastAutoOpenedGroupRef = useRef<string | undefined>(undefined)

  const timesheetRecord = Object.entries(timesheetData?.data?.docs ?? {}).sort(
    ([a], [b]) => b.localeCompare(a)
  )
  const notimesheetRecord = Object.keys(timesheetRecord).length === 0
  const firstTimesheetGroupKey = timesheetRecord[0]?.[0]
  const canCreateOnlineEntry = (shiftSetupData?.data?.shiftPlans || []).some(
    (plan) => plan.allowManualTimeEntry
  )

  useEffect(() => {
    if (
      firstTimesheetGroupKey &&
      lastAutoOpenedGroupRef.current !== firstTimesheetGroupKey
    ) {
      setActiveShiftCollapseKeys(firstTimesheetGroupKey)
      lastAutoOpenedGroupRef.current = firstTimesheetGroupKey
    }
  }, [firstTimesheetGroupKey])

  return (
    <div
      className="bg-white p-4 my-2"
      style={{ fontFamily: '"Circular Std", sans-serif' }}
    >
      <div className="block md:flex items-center justify-between mb-2 sm:mb-5">
        <H5 className="font-circular text:sm sm:ext-base text-padeBlack mb-3 font-semibold shrink-0">
          Timesheets
        </H5>
        <div className="flex items-center justify-center sm:justify-end gap-2">
          <div className="relative">
            <Select
              value={groupBy}
              onChange={(value) => {
                setGroupBy(value)
                setActiveShiftCollapseKeys("")
              }}
              suffixIcon={null}
              bordered={false}
              options={[
                { value: "day", label: "Group in day" },
                { value: "week", label: "Group in weeks" },
                { value: "month", label: "Group in months" },
              ]}
              className="border border-[#CAD5E2] rounded-md py-2 pr-4 flex-1 mr-1 [&_.ant-select-selection-item]:font-bold text-neutral-500 bg-white"
            />
            <img
              className="absolute right-2 top-2 ml-1"
              src={downIcon}
              width={20}
              height={20}
            />
          </div>
          <div className="border-r h-8 border-[#CAD5E2]" />
          {canCreateOnlineEntry && (
            <button
              onClick={() => setShowOptions(true)}
              className="w-fit flex-1 whitespace-nowrap py-1 px-4 flex items-center justify-center gap-2 bg-white border border-[#CAD5E2] rounded-md"
            >
              <img
                src={calenderPlus}
                width={15}
                height={15}
              />
              <span className="text-sm text-padeBlack whitespace-nowrap">
                Add manual entry
              </span>
            </button>
          )}
        </div>
      </div>

      <div
        className="border-none sm:border border-gray-300 rounded-t-lg overflow-hidden mt-2 sm:mt-5"
        style={{ scrollbarWidth: "none" }}
      >
        <div className="relative group">
          <div
            ref={scrollContainerRef}
            className="overflow-x-hidden sm:overflow-x-auto"
            style={{
              scrollbarWidth: "none",
            }}
          >
            <div className="bg-[#F8FAFC] border-b border-gray-300 min-w-max">
              <ul className="hidden sm:grid grid-cols-[100px_150px_150px_80px_80px_100px_150px_100px_100px] gap-3 items-center ml-2 py-3 px-2 text-slate-500 text-sm">
                <li className="sticky left-0 bg-[#F8FAFC] pl-2">Day</li>
                <li>Shift plan</li>
                <li>Shift period</li>
                <li>Clock-in</li>
                <li>Clock-out</li>
                <li>Method</li>
                <li>Flags</li>
                <li>Reason</li>
                <li>Status</li>
              </ul>
            </div>

            <LoadingRegion
              spinning={isLoadingTimesheet}
              placeholder={
                <CardListSkeleton
                  items={4}
                  lines={0}
                />
              }
            >
              {notimesheetRecord ? (
                <div className="mx-auto">
                  <img
                    src={EmptyImg}
                    className="w-full object-contain"
                  />
                </div>
              ) : (
                <div className="sm:min-w-max">
                  {timesheetRecord.map(([key, records]) => (
                    <div key={key}>
                      <div
                        className="flex justify-between items-center py-3 px-4 bg-white cursor-pointer border-b border-gray-200 min-w-[1110px]"
                        onClick={() =>
                          setActiveShiftCollapseKeys(
                            activeShiftCollapseKeys === key ? "" : key
                          )
                        }
                      >
                        <p className="text-sm text-padeBlack font-medium sticky left-0 bg-white">
                          {formatGroupLabel(key, groupBy)}
                        </p>
                        <button className="flex items-center gap-2 text-sm text-padeBlack sticky right-0 bg-white px-2">
                          {activeShiftCollapseKeys === key ? (
                            <>
                              <span>Collapse</span>
                              <img
                                src={upIcon}
                                width={20}
                                height={20}
                              />
                            </>
                          ) : (
                            <>
                              <span>Expand</span>
                              <img
                                src={downIcon}
                                width={20}
                                height={20}
                              />
                            </>
                          )}
                        </button>
                      </div>

                      {activeShiftCollapseKeys === key && (
                        <div className="border-none sm:border-t border-t-gray-300">
                          <div className="hidden sm:block">
                            {records.length === 0 ? (
                              <div className="mx-auto">
                                <img
                                  src={EmptyImg}
                                  className="w-full object-contain"
                                />
                              </div>
                            ) : (
                              sortByDate(records, "clockIn").map(
                                (record: IAttendanceRecord) => (
                                  <TimesheetRow
                                    key={record.id}
                                    day={formatDay(record.clockIn)}
                                    date={record.clockIn}
                                    timesheetQueryKey={timesheetQueryKey}
                                    shiftPlan={record.shiftPlan?.title}
                                    showAddReasonModal={showAddReasonModal}
                                    setShowAddReasonModal={
                                      setShowAddReasonModal
                                    }
                                    shiftPlanType={record.shiftPlan?.type}
                                    shiftPeriod={record.shiftPeriod?.title}
                                    clockIn={formatTime(record.clockIn)}
                                    clockOut={formatTime(record.clockOut)}
                                    flags={getFlags(record)}
                                    method={record.method}
                                    reason={record.reason}
                                    timesheetId={record.id}
                                    status={record.approvalStatus}
                                  />
                                )
                              )
                            )}
                          </div>
                          <div className="block sm:hidden">
                            <TimesheetCollapseRow
                              setShowAddReasonModal={setShowAddReasonModal}
                              records={records}
                              groupBy={groupBy}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </LoadingRegion>
          </div>
          {!isLoadingTimesheet && !notimesheetRecord && (
            <div
              onClick={() => {
                if (scrollContainerRef.current) {
                  scrollContainerRef.current.scrollTo({
                    left: scrollContainerRef.current.scrollWidth,
                    behavior: "smooth",
                  })
                }
              }}
              className="absolute right-0 top-0 bottom-0 sm:flex items-center bg-[rgba(15,_23,_43,_0.08)] w-[35px] justify-center hidden cursor-pointer pointer-events-auto"
            >
              <img
                src={rightIcon}
                className="w-[10px] h-[20px] opacity-100"
                alt="scroll"
              />
            </div>
          )}
        </div>
      </div>

      <ManualEntryModal
        open={showManualEntry}
        timesheetQueryKey={timesheetQueryKey}
        onClose={() => setShowManualEntry(false)}
        shiftSetup={shiftSetupData}
      />
      <TimesheetEntryOptionsModal
        open={showOptions}
        onClose={() => setShowOptions(false)}
        setShowManualEntry={setShowManualEntry}
      />
    </div>
  )
}
